import React, { useRef, useCallback, useEffect } from "react";
import { createButtonPressAnimation } from "../../../../utilities";
import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import {
  setAutoplaySpeed,
  setPendingAutoplaySpeed,
} from "../../../../State/actions";
import {
  selectAutoPlaySpeed,
  selectPendingAutoPlaySpeed,
  selectAutoPlayActive,
  selectIsSpinning,
  selectCriticalAnimationPlaying,
  selectHoldAndWinActive,
  selectHoldAndWinIsSpinning,
} from "../../../../State/selectors";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import { useHoldAndWinControl } from "../../../../Contexts/HoldAndWinControlContext";

// Turbo button configuration - matches UI button dimensions
const TURBO_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.965,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.75,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.7,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

const ButtonTurbo = ({ toolbar }) => {
  const dispatch = useDispatch();
  const { getResponsiveConfig, device, orientation, dimensions } =
    useDimensions();
  const { handleManualStop } = useReelControl();
  const { handleManualStop: handleHoldAndWinManualStop } = useHoldAndWinControl();

  // Get current speed and game state
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
  const pendingAutoPlaySpeed = useSelector(selectPendingAutoPlaySpeed);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const isSpinning = useSelector(selectIsSpinning);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const holdAndWinIsSpinning = useSelector(selectHoldAndWinIsSpinning);

  // Determine current effective speed (pending takes precedence if set)
  const currentSpeed = pendingAutoPlaySpeed || autoPlaySpeed;

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  // Add ref to track if button is currently processing a click
  const isProcessingClickRef = useRef(false);

  // Add ref to store timeout ID for proper cleanup
  const debounceTimeoutRef = useRef(null);

  // Determine if turbo is active (current or pending)
  const isTurboActive = currentSpeed === "turbo" || pendingAutoPlaySpeed === "turbo";

  const texture = PIXI.Assets.get(
    isTurboActive
      ? ASSET_NAME.icon_turbo_active
      : ASSET_NAME.icon_turbo
  );

  // Get responsive configuration from context
  const buttonConfig = getResponsiveConfig(TURBO_BUTTON_CONFIG, true);

  // Calculate responsive button position based on device and orientation
  // Uses toolbar width instead of full screen width
  const buttonPosition = (() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2 + buttonConfig.width * 1.65,
          y: -dimensions.height / 5.25,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width * 0.775,
          y: -dimensions.height / 2 + buttonConfig.height * 3.8,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2 + buttonConfig.width * 1.65,
          y: -dimensions.height / 5.1,
        };
      } else {
        return {
          x: toolbar.width - buttonConfig.width * 0.9,
          y: -buttonConfig.height * 1.65,
        };
      }
    } else {
      return {
        x: toolbar.width - buttonConfig.width,
        y: toolbar.height / 2,
      };
    }
  })();

  const handleClick = () => {
    // Disable button during critical animations
    if (criticalAnimationPlaying) {
      return;
    }

    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    // Toggle between "auto" and "turbo" speed
    const newSpeed = currentSpeed === "turbo" ? "auto" : "turbo";

    // If we're in the middle of a spin or autoplay is active, set as pending
    if (isSpinning || autoPlayActive || holdAndWinIsSpinning) {
      dispatch(setPendingAutoplaySpeed(newSpeed));

      // If switching TO turbo during an active spin, stop all unstopped reels immediately
      // This makes the transition to turbo faster by using manual stop timing
      if (newSpeed === "turbo") {
        if (holdAndWinActive && holdAndWinIsSpinning) {
          // Hold and Win feature is active and spinning - use Hold and Win manual stop
          console.log("ButtonTurbo: Triggering Hold and Win manual stop for faster turbo transition");
          handleHoldAndWinManualStop();
        } else if (isSpinning) {
          // Main game is spinning - use main game manual stop
          console.log("ButtonTurbo: Triggering main game manual stop for faster turbo transition");
          handleManualStop();
        }
      }
    } else {
      // Apply immediately if no spin is active
      dispatch(setAutoplaySpeed(newSpeed));
    }

    // Add press animation
    if (buttonSpriteRef.current) {
      createButtonPressAnimation(buttonSpriteRef.current);
    }

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    // Don't apply hover effect if button is disabled
    if (criticalAnimationPlaying) {
      return;
    }
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [
    buttonSpriteRef,
    brightnessFilter,
    criticalAnimationPlaying,
  ]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, [buttonSpriteRef]);

  // Remove hover effects when button becomes disabled
  useEffect(() => {
    if (
      criticalAnimationPlaying &&
      buttonSpriteRef.current
    ) {
      buttonSpriteRef.current.filters = [];
      isHoveredRef.current = false;
    }
  }, [criticalAnimationPlaying]);

  // Clean up hover effects and debounce timeout on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
      // Clear any pending debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Sprite
        ref={buttonSpriteRef}
        texture={texture}
        name="secondary_button_turbo"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={buttonPosition.x}
        y={buttonPosition.y}
        hitArea={new PIXI.Circle(0, 0, (buttonConfig.width / 2) * 1.8)}
        eventMode={
          criticalAnimationPlaying ? "none" : "static"
        }
        cursor={
          criticalAnimationPlaying ? "default" : "pointer"
        }
        alpha={criticalAnimationPlaying ? 0.5 : 1}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        resolution={2}
      />
    </>
  );
};

export default ButtonTurbo;
